import imageCompression from 'browser-image-compression';
// 프로젝트 공용 axios 인스턴스(인터셉터 등록 모듈)를 통해 Authorization 헤더가 자동 첨부되도록 함
import axios from 'axios';
import '@/api/repository/axios';

interface UploadResponse {
  success: boolean;
  url?: string;
  error?: string;
}

/**
 * 이미지를 서버에 업로드하는 함수
 * @param file - 업로드할 파일
 * @param endpoint - 업로드 엔드포인트 (기본값: '/api/upload/image')
 * @returns Promise<UploadResponse>
 */
export const uploadImage = async (
  file: File,
  endpoint: string = '/api/upload/image'
): Promise<UploadResponse> => {
  try {
    // 파일 타입 검증
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return {
        success: false,
        error: '지원하지 않는 이미지 형식입니다. (jpg, png, gif, webp만 가능)'
      };
    }

    const compressed = await imageCompression(file, {
      maxSizeMB: 2,
      maxWidthOrHeight: 2400,
      useWebWorker: true,
    });

    const formData = new FormData();
    formData.append('image', compressed, file.name);

    const { data } = await axios.post(endpoint, formData);

    // 백엔드 응답 구조: { code: 0, result: { url: "..." } }
    if (data.code === 0 && data.result?.url) {
      return {
        success: true,
        url: data.result.url
      };
    }

    return {
      success: false,
      error: data.message || '이미지 업로드에 실패했습니다.'
    };
  } catch (error) {
    console.error('이미지 업로드 실패:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '이미지 업로드에 실패했습니다.'
    };
  }
};

/** 압축 결과 MIME → 파일 확장자. 서버가 파일명 확장자(jpg/jpeg/png/pdf)로 형식을 검증하므로 결과 형식과 맞춰야 한다 */
const EXTENSION_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
};

/**
 * 면허증 사진(jpg/png)을 업로드 전에 압축한다. PDF 등 그 외 형식은 손대지 않고 그대로 돌려준다.
 *
 * 폰 원본 사진은 수 MB~10MB 이상이라 모바일 업로드가 느리고 10MB 한도에 걸린다.
 * 공용 업로드(uploadImage)와 같은 옵션(긴 변 2400px)이면 면허증 글자 판독에는 충분하다.
 * 업로드는 multipart 바이너리 그대로 한다 — base64 로 바꾸면 오히려 약 33% 커진다.
 *
 * 압축이 실패하거나 원본보다 커지면 원본을 그대로 쓴다 — 압축 때문에 신청이 막히면 안 된다.
 */
export const compressLicenseImage = async (file: File): Promise<File> => {
  if (!(file.type in EXTENSION_BY_MIME)) return file;
  try {
    const compressed: Blob = await imageCompression(file, {
      maxSizeMB: 2,
      maxWidthOrHeight: 2400,
      useWebWorker: true,
    });
    if (compressed.size >= file.size) return file;
    // 결과 형식에 맞는 확장자로 파일명을 다시 붙인다 (서버 검증이 파일명 확장자 기준)
    const outputType: string = compressed.type || file.type;
    const extension: string = EXTENSION_BY_MIME[outputType] ?? EXTENSION_BY_MIME[file.type];
    const baseName: string = file.name.replace(/\.[^.]+$/, '') || 'license';
    return new File([compressed], `${baseName}.${extension}`, { type: outputType in EXTENSION_BY_MIME ? outputType : file.type });
  } catch (error) {
    console.error('면허증 이미지 압축 실패 — 원본으로 업로드합니다:', error);
    return file;
  }
};

/**
 * 임시 개발용 이미지 업로드 함수 (더미 데이터)
 * 실제 서버 구현 전까지 사용
 */
export const uploadImageMock = async (file: File): Promise<UploadResponse> => {
  // 실제 파일 검증
  if (!file.type.startsWith('image/')) {
    return {
      success: false,
      error: '이미지 파일만 업로드 가능합니다.'
    };
  }

  // 파일 크기 검증
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    return {
      success: false,
      error: '파일 크기는 5MB를 초과할 수 없습니다.'
    };
  }

  // 업로드 시뮬레이션 (2초 지연)
  await new Promise(resolve => setTimeout(resolve, 2000));

  // 더미 URL 반환 (실제로는 서버에서 받은 URL)
  const timestamp = Date.now();
  const mockUrl = `https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80&t=${timestamp}`;
  
  return {
    success: true,
    url: mockUrl
  };
};

/**
 * Base64를 File 객체로 변환하는 함수
 */
export const base64ToFile = (base64: string, filename: string): File => {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new File([u8arr], filename, { type: mime });
};

/**
 * 파일을 Base64로 변환하는 함수
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};